import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { CASE_DOCUMENT_STATUS, CASE_FILE_KIND, CaseDocumentFileModel, CaseModel } from "@shared/models";
import { selectors } from "@containers/Cases/store";
import { CASE_DOCUMENT_MODAL_STAGE, MEDICAL_FIELD_LABELS } from "@containers/Cases/constants";
import { useCaseDocumentActions, useCaseDocumentParsingFailed } from "@containers/Cases/hooks";
import { getSubjectName, joinWithAnd } from "@containers/Cases/utils/caseDocuments";

import MedicalRecordsUploadStep from "./MedicalRecordsUploadStep";
import MedicalRecordsParsingStep from "./MedicalRecordsParsingStep";
import MedicalRecordsResultStep from "./MedicalRecordsResultStep";

const NO_FILES: CaseDocumentFileModel[] = [];

const FIELD_NAMES = Object.values(MEDICAL_FIELD_LABELS);

const getSubtitle = (caseObj: CaseModel | null) => {
  const subjectName = getSubjectName(caseObj);
  const fields = joinWithAnd(FIELD_NAMES);

  return subjectName ? `${subjectName} · text is added to ${fields}` : `Text is added to ${fields}`;
};

interface UploadMedicalRecordsModalProps {
  caseId: number;
  initialStage?: CASE_DOCUMENT_MODAL_STAGE;
  /** Opens the upload step on the current files, to replace them */
  replace?: boolean;
  onClose: () => void;
}

const UploadMedicalRecordsModal: FC<UploadMedicalRecordsModalProps> = ({
  caseId,
  initialStage = CASE_DOCUMENT_MODAL_STAGE.UPLOAD,
  replace = false,
  onClose,
}) => {
  const [stage, setStage] = useState(initialStage);
  const [isReplacing, setIsReplacing] = useState(replace);
  const caseObj = useSelector(selectors.getCase());
  const caseDocuments = useSelector(selectors.getCaseDocuments());
  const { chooseValues } = useCaseDocumentActions(caseId);

  const medicalRecords = caseDocuments.find(({ kind }) => kind === CASE_FILE_KIND.MEDICAL);
  const isFailed = useCaseDocumentParsingFailed(medicalRecords);
  // The case page shows a set that couldn't be read at all, with its Retry and Replace.
  const hasNoResult = stage === CASE_DOCUMENT_MODAL_STAGE.STATUS && (!medicalRecords || isFailed);

  useEffect(() => {
    if (hasNoResult) onClose();
  }, [hasNoResult, onClose]);

  if (stage === CASE_DOCUMENT_MODAL_STAGE.UPLOAD) {
    return (
      <MedicalRecordsUploadStep
        caseId={caseId}
        documentFiles={medicalRecords?.files ?? NO_FILES}
        replace={isReplacing}
        subtitle={getSubtitle(caseObj)}
        onClose={onClose}
        onParse={() => setStage(CASE_DOCUMENT_MODAL_STAGE.STATUS)}
      />
    );
  }

  if (!medicalRecords || isFailed) return null;

  if (medicalRecords.status === CASE_DOCUMENT_STATUS.PARSING) {
    return <MedicalRecordsParsingStep caseDocument={medicalRecords} onClose={onClose} />;
  }

  return (
    <MedicalRecordsResultStep
      caseId={caseId}
      caseDocument={medicalRecords}
      onClose={onClose}
      onReplaceFile={() => {
        setIsReplacing(true);
        setStage(CASE_DOCUMENT_MODAL_STAGE.UPLOAD);
      }}
      onChooseValues={chooseValues && (() => chooseValues())}
    />
  );
};

export default UploadMedicalRecordsModal;
